import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-cycle
import { RootState } from '../store';
import data from '../../json/sections';
import {storageFactory} from '../../helpers/storage';
const local = storageFactory(() => localStorage);

const styleData = {
  colors: {
    primary: '#4EAC93',
    accent: '#4A50C0',
    extra: '#E29FE1',
    white: '#FDFAF2',
    font: '#1F2041',
    header: '#1F2041',
    footer: '#1F2041',
    transparent: 'rgba(0,0,0,1)',
    colorHeaderLink: '#FFFFFF',
  },
  fonts: {
    headings: 'Circe',
    main: 'Roboto',
  },
  roundings: {
    big: 32,
    small: 10,
  },
  textSize: {
    header: 48,
    body: 16,
    menu: 16,
    locMenuSize: false,
    capitalizeMenuLink: true,
  },
  header: {
    // opacity: 1,
    bgBehavior: 'default',
    firstButtonColor: '#4A50C0',
    firstButtonFontColor: '#FDFAF2',
    firstButtonOpacity: 1,
    firstButtonGradientActive: 0,
    secondButtonColor: '#4A50C0',
    secondButtonFontColor: '#FDFAF2',
    secondButtonOpacity: 1,
    secondButtonGradientActive: 0,
    thirdButtonColor: '#4A50C0',
    thirdButtonFontColor: '#FDFAF2',
    thirdButtonOpacity: 1,
    thirdButtonGradientActive: 0,
    isHeaderVisible: false,
    hasHeaderGradient: true,
    isLoginVisible: true,
    isRegistrationVisible: true,
  },
  preloader: {
    color: '#4EAC93',
    speed: '1.1',
    size: '100',
    thickness: '6',
  },
  footer: {
    isFooterVisible: false,
    isLogoHidden: false,
  },
  cookie: {
    isCookiePopupVisible: true,
    fontColor: '',
    buttonColor: '',
    buttonFontColor: '',
  },
  loginPopup: {
    buttonColor: '',
    buttonFontColor: '',
  },
  playerSettings: {
    playerWidth: '50',
    playerMobileWidth: '100',
  },
};

const initialState: State = {
  sections: data,
  style: styleData,
  socials: [],
  editBlock: null,
  fonts: [],
  internalization: false,
  activeLangState: 'ru',
  isAwailableLocaleSwitcher: false,
  uniqueWatchKey: '',
};

const copySectionParams = (dstParams: ParamsDto | undefined, srcParams: ParamsDto) => {
  if (!dstParams) {
    return
  }
  if (srcParams.checked !== undefined) {
    dstParams.checked = srcParams.checked
  }
  if (srcParams.value !== undefined) {
    dstParams.value = srcParams.value
  }
  if (srcParams.isRedifined !== undefined) {
    dstParams.isRedifined = srcParams.isRedifined
  }
  if (srcParams.blank !== undefined) {
    dstParams.blank = srcParams.blank
  }
  if (srcParams.linkType !== undefined) {
    dstParams.linkType = srcParams.linkType
  }
  if (srcParams.hidden !== undefined) {
    dstParams.hidden = srcParams.hidden
  }
}

const copySection = (dstSection: DataDto | undefined, srcSection: DataDto) => {
  if (!dstSection || dstSection.id !== srcSection.id) return;
  dstSection.name = srcSection.name
  dstSection.name_en = srcSection.name_en
  dstSection.page = srcSection.page
  dstSection.visible = srcSection.visible
  if (srcSection.component !== undefined) {
    dstSection.component = srcSection.component
  }
  if (srcSection.isNavigational !== undefined) {
    dstSection.isNavigational = srcSection.isNavigational
  }
  srcSection.params?.forEach(el => copySectionParams(dstSection.params?.find(dstParam => dstParam.id === el.id), el))
}

const reorderSections = (sectionsArray: DataDto[], externalSectionsArray: DataDto[]) => {
  let currentElementToSwap = 0;
  for (let externalIdx = 0; externalIdx < externalSectionsArray.length; externalIdx++) {
    const externalSection = externalSectionsArray[externalIdx];
    const internalId = externalSection.id;
    const sectionInMainArray = sectionsArray.findIndex(section => section.id === internalId);
    if (sectionInMainArray < 0) {
      continue;
    }
    if (sectionInMainArray !== currentElementToSwap) {
      const tmp = sectionsArray[sectionInMainArray];
      sectionsArray[sectionInMainArray] = sectionsArray[currentElementToSwap];
      sectionsArray[currentElementToSwap] = tmp;
    }
    currentElementToSwap += 1;
  }
}

export const sections = createSlice({
  name: 'sections',
  initialState,
  reducers: {
    toggleVisibility: (state, action: PayloadAction<number>) => {
      const { payload } = action;

      const tSec = state.sections.find((x) => x.id === payload);
      if (tSec) {
        tSec.visible = !tSec.visible;
      }
    },
    changeParam: (state, action: PayloadAction<ChangeProps>) => {
      const { payload } = action;

      const tSec = state.sections.find((x) => x.id === payload.sectionId);
      if (tSec) {
        const tParam = tSec.params?.find((x) => x.id === payload.pid);
        if (tParam && typeof payload.value === 'string') {
          tParam.value = payload.value;
          if (tParam.readStyleFromId && payload.value) {
            tParam.isRedifined = true
          }
        }

        if (tParam?.hasChilds) {
          tSec.params?.forEach((param) => {
            if (param.parent === tParam.key) {
              if (param.isParentValue === payload.value) {
                param.visible = true;
              } else {
                param.visible = false;
              }
            }
          });
        }
      }
    },
    changeBooleanParam: (state, action: PayloadAction<ChangeProps>) => {
      const { payload } = action;

      const tSec = state.sections.find((x) => x.id === payload.sectionId);
      if (tSec) {
        const tParam = tSec.params?.find((x) => x.id === payload.pid);
        if (tParam && typeof payload.value === 'boolean') {
          tParam.checked = payload.value;
        }
      }
    },
    updateMoreDetailsButtonData: (state, action: PayloadAction<any>) => {
      const { payload } = action;

      const tSec = state.sections.find((x) => x.id === payload.sectionId);
      if (tSec) {
        const tParam = tSec.params?.find((x) => x.id === payload.pid);
        if (tParam) {
          tParam[payload.field as keyof ParamsDto] = payload.value;
        }
      }
    },
    resetColorToDefault: (state, action: PayloadAction<{sectionId: number, pid: number}>) => {
      const { payload } = action;

      const tSec = state.sections.find((x) => x.id === payload.sectionId);
      if (tSec) {
        let tParam = tSec.params?.find((x) => x.id === payload.pid);
        if (tParam) {
          tParam.isRedifined = false;
          const styleParts = (tParam.readStyleFromId as string).split('.', 2)
          const styleSec = styleParts[0], styleId = styleParts[1]
          tParam.value = state.style[styleSec][styleId]
        }
      }
    },
    changeOrder: (state, action: PayloadAction<DataDto[]>) => {
      state.sections = action.payload;
    },
    changeSectionsAll: (state, action: PayloadAction<DataDto[]>) => {
      action.payload.forEach(srcSection => {
        copySection(state.sections.find(stateSection => stateSection.id === srcSection.id), srcSection)
      })
      reorderSections(state.sections, action.payload);
      state.sections.map(section => section.params).forEach(secParams => secParams?.forEach(param => {
        if (!param.readStyleFromId || param.isRedifined) {
          return
        }
        const styleParts = param.readStyleFromId.split('.', 2)
        const styleSec = styleParts[0], styleId = styleParts[1]
        param.value = state.style[styleSec][styleId]
      }))
    },
    changeStyleParam: (state, action: PayloadAction<ChangeStyleProps>) => {
      const { payload } = action;
      state.style[payload.section][payload.id] = payload.value;
      const fullId = payload.section + '.' + payload.id
      state.sections.forEach(section => section.params?.forEach(param => {
        if (param.readStyleFromId === fullId && !param.isRedifined && typeof payload.value === 'string') {
          param.value = payload.value
        }
      }))
    },
    changeStyleAll: (state, action: PayloadAction<object>) => {
      const result = Object.keys(styleData).reduce((acc: {[key: string]: object} , key: string): {[key: string]: object} => {
        if (key in action.payload) {
          const keyData = JSON.parse(JSON.stringify((action.payload as {[key: string]: {[key: string]: any}})[key]));
          const styleDataKeyObject = (styleData as {[key: string]: object})[key];
          const keyDataKeys = Object.keys(keyData);
          const styleDataKeyObjectKeys = Object.keys(styleDataKeyObject);

          for (const keyName of styleDataKeyObjectKeys) {
            if (!keyDataKeys.includes(keyName)) {
              keyData[keyName] = (styleDataKeyObject as {[key: string]: object})[keyName];
            }
          }

          acc[key] = {};

          for (const idx in keyData) {
            if (idx in keyData || (typeof keyData[idx] === 'number' && keyData[idx] !== 'undefined')) {
              (acc[key] as {[key: string]: object})[idx] = keyData[idx];
            } else {
              (acc[key] as {[key: string]: object})[idx] = ((styleData as {[key: string]: object})[key] as {[key: string]: object})[idx];
            }
          }
        } else {
          acc[key] = {};
          acc[key] = (styleData as {[key: string]: object})[key];
        }

        return acc;
      }, {});

      state.style = result;
    },
    changeSocialParam: (state, action: PayloadAction<ChangeSocialProps>) => {
      const { payload } = action;

      const tSocial = state.socials.find((x) => x.id === payload.id);
      if (tSocial) {
        tSocial.link = payload.value;
      }
    },
    changeSocialAll: (state, action: PayloadAction<SocialDto[]>) => {
      state.socials = action.payload;
    },
    addSocialParam: (state, action: PayloadAction<AddSocialDto>) => {
      const { payload } = action;

      // const newId = Math.max(...state.socials.map((x) => x.id)) + 1;
      let newId = 0;
      if (!state.socials.length) {
        newId = state.socials.length + 1;
      } else {
        newId = Math.max(...state.socials.map((x) => x.id)) + 1;
      }
      const newSocial = {
        id: newId,
        icon: payload.icon,
        link: '',
      };
      state.socials = [...state.socials, newSocial];
    },
    removeSocialParam: (state, action: PayloadAction<RemoveSocialDto>) => {
      const { payload } = action;

      state.socials = state.socials.filter((x) => x.id !== payload.id);
    },
    editSelectedBlock: (state, action: PayloadAction<number>) => {
      const { payload } = action;
      state.editBlock = payload;
    },
    changeBlockName: (state, action: PayloadAction<NewBlockName>) => {
      const { payload } = action;
      state.editBlock = null;

      const block = state.sections.find((x) => x.id === payload.id);
      if (block) {
        block.name = payload.title.name;
        block.name_en = payload.title.name_en;
      }
    },
    updateFontsList: (state, action: PayloadAction<Font[]>) => {
      const { payload } = action;
      state.fonts = payload;
    },
    updateInternalizationState: (state, {payload}: PayloadAction<{locales: Array<string>, eventKey: string}>) => {
      let isInternalizationActive = false;
      const {locales, eventKey} = payload;

      if (locales.length > 1) {
        isInternalizationActive = true;
        state.isAwailableLocaleSwitcher = true;
      }

      state.internalization = isInternalizationActive;
      let language = window.navigator ?
        (window.navigator.language || (window.navigator as {systemLanguage?: string;})['systemLanguage'] || (window.navigator as {userLanguage?: string;})['userLanguage'])
      : 'ru';
      language = language && language.substr(0, 2).toLowerCase();

      const localStorageLocale = local.getItem(`userCurrentLocale${eventKey}`);

      if (state.internalization) {
        if (state.isAwailableLocaleSwitcher) {
          if (language) {
            state.activeLangState = language === 'ru' ? language : 'en';
          }

          if (!localStorageLocale) {
            local.setItem(`userCurrentLocale${eventKey}`, state.activeLangState);
          } else {
            state.activeLangState = localStorageLocale;
          }
        } else {
          state.activeLangState = locales[0];
          local.setItem(`userCurrentLocale${eventKey}`, state.activeLangState);
        }
      } else {
        // if (language) {
        //   state.activeLangState = language === 'ru' ? language : 'en';
        //   local.setItem(`userCurrentLocale${eventKey}`, state.activeLangState);
        // }
        state.activeLangState = locales[0];
        local.setItem(`userCurrentLocale${eventKey}`, state.activeLangState);
      }
    },
    toggleLanguage: (state, {payload}: PayloadAction<{locale: string, eventKey: string}>) => {
      const {locale, eventKey} = payload;

      if (state.internalization) {
        state.activeLangState = locale;
        local.setItem(`userCurrentLocale${eventKey}`, state.activeLangState);
      }
    },
    updateUniqueWatchKey: (state, {payload}: PayloadAction<string>) => {
      state.uniqueWatchKey = payload;
    },
    toggleNavigationVisibility: (state, action: PayloadAction<number>) => {
      const { payload } = action;
      const tSec = state.sections.find((x) => x.id === payload);
      if (tSec) {
        tSec.isNavigational = !tSec.isNavigational;
      }
    },
  },
});

export const {
  toggleVisibility,
  changeParam,
  changeOrder,
  changeSectionsAll,
  changeStyleParam,
  changeStyleAll,
  changeSocialParam,
  changeSocialAll,
  addSocialParam,
  removeSocialParam,
  editSelectedBlock,
  changeBlockName,
  changeBooleanParam,
  updateMoreDetailsButtonData,
  updateFontsList,
  resetColorToDefault,
  toggleLanguage,
  updateInternalizationState,
  updateUniqueWatchKey,
  toggleNavigationVisibility,
} = sections.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const getAllSections = (state: RootState) => state.sections.sections;
export const getAllStyle = (state: RootState) => state.sections.style;
export const getAllSocials = (state: RootState) => state.sections.socials;
export const navSections = (state: RootState) =>
  state.sections.sections.filter((x: any) => x.visible && x.isNavigational);
export const getAllFonts = (state: RootState) => state.sections.fonts;
export const internalizationState = (state: RootState) => (
  {
    internalization: state.sections.internalization,
    activeLangState: state.sections.activeLangState,
    isAwailableLocaleSwitcher: state.sections.isAwailableLocaleSwitcher,
  }
);
export const getUniqueWatchKey = (state: RootState): string => state.sections.uniqueWatchKey;

export const isSectionVisible = (id: number) =>
  createSelector(
    (state: RootState) => state.sections.sections,
    (state) => {
      const tSection = state.find((x: any) => x.id === id);
      if (tSection && ['RegisterPage', 'LivePage', 'SpeakersPage', 'StaticPage'].includes(tSection.page)) {

        return true
      }

      return tSection ? tSection.visible : true;
    }
  );

export const getEditBlock = () =>
  createSelector(
    (state: RootState) => state,
    (state) => state.sections.editBlock
  );

export const getSectionBackground = (id: number) =>
  createSelector(
    (state: RootState) => state.sections.sections,
    (state) => {
      const tSection = state.find((x: any) => x.id === id);
      let bgValue;
      if (tSection && tSection.params) {
        const param = tSection.params.find((x: any) => x.type === 'background');
        if (param) bgValue = param.value;
      }
      return bgValue;
    }
  );

export const getSectionClassNames = (id: number) =>
  createSelector(
    (state: RootState) => state.sections.sections,
    (state) => {
      const tSection = state.find((x: any) => x.id === id);
      let classNames;
      if (tSection && tSection.params) {
        classNames = tSection.params.reduce((acc: Array<string>, x: any) => {
          if (x.className && x.checked) {
            acc.push(x.className);
          }
          return acc;
        }, []);
      }
      return classNames;
    }
  );

const sectionsReducer = sections.reducer;
export default sectionsReducer;

interface State {
  sections: DataDto[];
  style: any;
  socials: SocialDto[];
  editBlock: number | null;
  fonts: Font[];
  internalization: boolean;
  activeLangState: string;
  isAwailableLocaleSwitcher: boolean;
  uniqueWatchKey: string;
}

export interface DataDto {
  name: string;
  name_en?: string;
  id: number;
  visible: boolean;
  isNavigational?: boolean;
  page: string;
  component?: string;
  params?: ParamsDto[];
}

export interface ParamsDto {
  id?: number;
  type?: string;
  value?: string;
  className?: string;
  checked?: boolean;
  label?: string;
  label_en?: string;
  readStyleFromId?: string;
  isRedifined?: boolean;
  params?: any;
  key?: string;
  visible?: boolean;
  parent?: string;
  isParentValue?: string;
  hasChilds?: boolean;
  linkType?: string;
  blank?: boolean;
  hidden?: boolean;
}

type ParamsDtoKeys = keyof ParamsDto;

export interface SocialDto {
  id: number;
  icon: string;
  link: string;
}

interface ChangeProps {
  value: string | boolean;
  sectionId: number;
  pid: number;
}

interface ChangeStyleProps {
  value: string | number | boolean;
  section: string;
  id: string;
}

interface ChangeSocialProps {
  value: string;
  id: number;
}

interface AddSocialDto {
  icon: string;
}

interface RemoveSocialDto {
  id: number;
}

interface NewBlockName {
  id: number;
  // title: string;
  title: {
    name: string;
    name_en: string;
  };
}

interface Font {
  normalizedTitle: string;
  path: string;
  title: string;
}

interface Font {
  normalizedTitle: string;
  path: string;
  title: string;
}
